import { Location } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Observable,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  of,
  take,
} from 'rxjs';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import {
  LegalPersonalityEnum,
  LegalPersonalityIdEnum,
  getLegalPersonalityDescriptionByValue,
} from 'src/app/domain/enums/legal-personality.enum';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { getContributorTypeDescriptionByValue } from 'src/app/domain/registration/enums/contributor-type.enum';
import {
  FiscalEnvironmentEnum,
  getFiscalEnvironmentDescriptionByValue,
} from 'src/app/domain/registration/enums/fiscal-environment.enum';
import { getRegimeTypeDescriptionByValue } from 'src/app/domain/registration/enums/regime-type.enum';
import {
  SystemTypeEnum,
  getSystemTypeDescriptionByValue,
} from 'src/app/domain/registration/enums/system-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { ICustomer } from 'src/app/domain/customer/ICustomer';
import { AbstractCrudCES } from 'src/app/shared/components/crud/abstract-crud-ces';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { AuthService } from 'src/app/views/auth/shared/auth.service';
import { CES_CONFIG_FIELDS } from '../crud-config';
import { CustomerService } from '../crud-service';
import { FormDataCreate } from './formDataBuild/form-data-create';
import { FormDataUpdate } from './formDataBuild/form-data-update';
import { PermissionService } from 'src/app/shared/permission.service';
import { PlanSelectionWizardComponent } from '../../plans/plan-selection-wizard/plan-selection-wizard.component';
import { PartnerService } from '../../partners/crud-service';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { S3_URL_BASE } from 'src/environments/environment';
import { formatDocument } from 'src/app/shared/utils/document-formatter';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import {
  CustomerBranch,
  CustomerMainAddress,
  normalizeBranches,
  syncMatrizAddress,
} from '../customer-branch.util';
import { CustomerResponsible } from '../customer-responsible.util';
import {
  CUSTOMER_SECTIONS,
  CustomerSectionKey,
  customerSectionIndex,
} from '../customer-sections';

@Component({
  selector: 'app-customer-ces',
  templateUrl: './crud-ces.component.html',
  styleUrls: ['./crud-ces.component.sass'],
})
export class CustomerCESComponent
  extends AbstractCrudCES<ICustomer>
  implements OnInit, OnDestroy, AfterViewInit
{
  public consultantSelected: any;
  public consultantSelectedFire: EventEmitter<any> = new EventEmitter();

  public partnerSelected: any;
  public partnerSelectedFire: EventEmitter<any> = new EventEmitter();

  public representativeSelected: any;
  public representativeSelectedFire: EventEmitter<any> = new EventEmitter();

  public counterSelected: any;
  public counterSelectedFire: EventEmitter<any> = new EventEmitter();

  public unitSelected: any;
  public unitSelectedFire: EventEmitter<any> = new EventEmitter();

  public filteredCities: any[] = [];
  private legalPersonalitySub: Subscription | undefined;
  customerPlansIn: MatTableDataSource<any> = new MatTableDataSource<any>([]);
  customerPlansOut: any;

  hasPendingPlan: boolean = false;

  private planSelectionDialogRef: MatDialogRef<PlanSelectionWizardComponent> | null =
    null;

  public isDev: boolean = false;

  /** Seção ativa. A ordem vive em CUSTOMER_SECTIONS. */
  public sectionTabIndex: number = 0;
  public readonly customerSections = CUSTOMER_SECTIONS;
  public branches: CustomerBranch[] = [];
  public responsibleDrafts: CustomerResponsible[] = [];
  private addressSyncSub = new Subscription();

  // Propriedade para controlar o modo manual de endereço
  public isManualAddressMode: boolean = false;

  get fiscalEnvironmentEnum() {
    return FiscalEnvironmentEnum;
  }

  back() {
    const currentUrl = window.location.href;

    const urlParams = new URLSearchParams(window.location.search);
    const fromParam = urlParams.get('from');

    this.route.queryParams.pipe(take(1)).subscribe((params) => {
      const fromSource = fromParam || params['from'];

      if (fromSource === 'partner-dashboard') {
        this.router.navigate(['/partner-dashboard']);
        return;
      }

      if (fromSource === 'dashboard') {
        this.router.navigate(['/dashboard']);
        return;
      }

      if (fromSource === 'client-per-plans') {
        this.router.navigate(['/accounts-dashboard']);
        return;
      }

      this.router.navigate(['customers'], {
        queryParams: { preserveFilter: 'true' },
      });
    });
  }

  override config: IFieldConfig[] = CES_CONFIG_FIELDS;

  public isAllowChangeDigitalCertificate() {
    let digitalCertificate = this.form.get('digitalCertificate')?.getRawValue();
    return typeof digitalCertificate === 'string' &&
      this.authService.isPartner()
      ? TypeCesEnum.SHOW
      : this.typeCes;
  }

  public isAllowChangeContract(): TypeCesEnum {
    let contract = this.form.get('contract')?.getRawValue();

    return typeof contract === 'string' && this.authService.isPartner()
      ? TypeCesEnum.SHOW
      : this.typeCes;
  }

  public isAllowChangeLicense(): TypeCesEnum {
    let license = this.form.get('license')?.getRawValue();

    return typeof license === 'string' && this.authService.isPartner()
      ? TypeCesEnum.SHOW
      : this.typeCes;
  }

  public isAllowChangeLastBackup(): TypeCesEnum {
    let lastBackup = this.form.get('lastBackup')?.getRawValue();

    return typeof lastBackup === 'string' && this.authService.isPartner()
      ? TypeCesEnum.SHOW
      : this.typeCes;
  }

  setCustomerPlansOut($event: any) {
    this.customerPlansOut = $event;
  }

  onPendingPlanChange(hasPending: boolean) {
    this.hasPendingPlan = hasPending;
    this.cdr.detectChanges();
  }

  setFields() {
    this.consultantSelected = this.data?.consultant;
    this.consultantSelectedFire.emit(this.consultantSelected);

    this.partnerSelected = this.data?.partner;
    this.partnerSelectedFire.emit(this.partnerSelected);

    this.representativeSelected = this.data?.representative;
    this.representativeSelectedFire.emit(this.representativeSelected);

    this.counterSelected = this.data?.counter;
    this.counterSelectedFire.emit(this.counterSelected);

    this.unitSelected = this.data?.unit;
    this.unitSelectedFire.emit(this.unitSelected);

    this.form.patchValue({
      ...this.data,
      legalPersonality: getLegalPersonalityDescriptionByValue(
        this.data.legalPersonality
      ),
      contributorType: getContributorTypeDescriptionByValue(
        this.data.contributorType
      ),
      regime: getRegimeTypeDescriptionByValue(this.data.regime),
      consultant:
        this.data?.consultant?.companyName || this.data?.consultant?.name,
      partner: this.data?.partner?.companyName || this.data?.partner?.name,
      representative:
        this.data?.representative?.companyName ||
        this.data?.representative?.name,
      counter: this.data?.counter?.companyName || this.data?.counter?.name,
      unit: this.data?.unit?.companyName || this.data?.unit?.name,
      fiscalEnvironment: getFiscalEnvironmentDescriptionByValue(
        this.data?.fiscalEnvironment
      ),
      createdAt: this.data?.createdAt ? new Date(this.data.createdAt) : null,
    });

    this.onChangeState(this.data.state);
    this.form.get('city')?.setValue(this.data.city);
    this.customerPlansIn = new MatTableDataSource<any>(
      this.data.customerPlans
        .map((v: any) => {
          return {
            ...v,
            system: v.system?.name,
            type: getSystemTypeDescriptionByValue(v.type),
          };
        })
        .sort((a: any, b: any) => {
          if (a.type === SystemTypeEnum.SYSTEM) return 1;
          if (b.type === SystemTypeEnum.SYSTEM) return -1;
          return 0;
        })
        .sort((a: any, b: any) => {
          if (a.active) return -1;
          if (b.active) return 1;
          return 0;
        })
    );
    this.customerPlansOut = new MatTableDataSource<any>(
      this.data.customerPlans.map((v: any) => {
        return {
          ...v,
          system: v.system?.id,
          type: v.type,
        };
      })
    );

    this.form.get('code')?.disable();
    this.branches = normalizeBranches(
      (this.data as any)?.additionalBranches,
      this.mainAddressFromForm()
    );
  }

  isSection(key: CustomerSectionKey): boolean {
    return this.sectionTabIndex === customerSectionIndex(key);
  }

  selectCustomerSection(index: number): void {
    if (index < 0 || index >= this.customerSections.length) return;
    this.sectionTabIndex = index;
  }

  get customerContext(): string {
    return this.typeCes === TypeCesEnum.NEW ? 'NOVO CLIENTE' : 'CADASTRO DO CLIENTE';
  }

  get customerHeading(): string {
    if (this.typeCes === TypeCesEnum.NEW) return 'Cadastrar cliente';
    return this.customerDisplayName || this.title;
  }

  get customerSubtitle(): string {
    if (this.typeCes === TypeCesEnum.NEW) {
      return 'Preencha as seções para criar um cadastro completo.';
    }
    return this.customerDocumentLabel || 'Dados cadastrais e vínculos do cliente';
  }

  /** Nome ou razão social do cliente já cadastrado, conforme o tipo. */
  get customerDisplayName(): string {
    if (this.isLegalPerson) {
      return this.fieldText('companyName') || this.fieldText('name');
    }
    return this.fieldText('name') || this.fieldText('companyName');
  }

  /** CPF ou CNPJ formatado, com o rótulo do tipo de pessoa. */
  get customerDocumentLabel(): string {
    const raw = this.isLegalPerson ? this.fieldText('cnpj') : this.fieldText('cpf');
    if (!raw) return '';
    return `${this.isLegalPerson ? 'CNPJ' : 'CPF'} ${formatDocument(raw)}`;
  }

  /**
   * Foto escolhida no cadastro, também exibida no canto direito do cabeçalho.
   * Aceita base64 (seleção local) ou caminho relativo do armazenamento.
   */
  get customerHeaderImage(): string {
    const raw = this.form?.get('image')?.value;
    if (typeof raw !== 'string' || !raw.trim()) return '';
    const value = raw.trim();
    if (
      value.includes('base64') ||
      value.startsWith('data:') ||
      value.startsWith('blob:') ||
      value.startsWith('http://') ||
      value.startsWith('https://')
    ) {
      return value;
    }
    return `${S3_URL_BASE}${value.replace(/^\//, '')}`;
  }

  private get isLegalPerson(): boolean {
    return (
      this.form?.get('legalPersonality')?.value ===
      LegalPersonalityEnum.PESSOA_JURIDICA
    );
  }

  private fieldText(field: string): string {
    const value = this.form?.get(field)?.value;
    return typeof value === 'string' ? value.trim() : '';
  }

  private mainAddressFromForm(): CustomerMainAddress {
    const personality = this.form?.get('legalPersonality')?.value;
    const document =
      personality === LegalPersonalityEnum.PESSOA_JURIDICA
        ? this.form?.get('cnpj')?.value
        : this.form?.get('cpf')?.value;
    return {
      cep: this.form?.get('cep')?.value,
      address: this.form?.get('address')?.value,
      addressNumber: this.form?.get('addressNumber')?.value,
      addressComplement: this.form?.get('addressComplement')?.value,
      neighborhood: this.form?.get('neighborhood')?.value,
      state: this.form?.get('state')?.value,
      city: this.form?.get('city')?.value,
      document,
      stateRegistration: this.form?.get('stateRegistration')?.value,
    };
  }

  private watchMainAddress(): void {
    this.addressSyncSub.unsubscribe();
    this.addressSyncSub = new Subscription();
    [
      'cep',
      'address',
      'addressNumber',
      'addressComplement',
      'neighborhood',
      'state',
      'city',
      'cpf',
      'cnpj',
      'stateRegistration',
    ].forEach((field) => {
      const control = this.form.get(field);
      if (!control) return;
      this.addressSyncSub.add(
        control.valueChanges.subscribe(() => {
          const current = this.branches.length
            ? this.branches
            : normalizeBranches([], this.mainAddressFromForm());
          this.branches = syncMatrizAddress(current, this.mainAddressFromForm());
        })
      );
    });
  }

  getCreatedAt() {
    return this.data?.createdAt;
  }

  getUpdatedAt() {
    return this.data?.updatedAt;
  }

  constructor(
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
    private service: CustomerService,
    private partnerService: PartnerService,
    public override fb: FormBuilder,
    private dialog: MatDialog,
    private toastService: ToastService,
    private location: Location,
    public authService: AuthService,
    public permissionService: PermissionService
  ) {
    super(fb);
    this.typeCes = route.snapshot.data['type'];
    this.title = `${this.typeCes} ${this.service.title.toUpperCase()}`;
  }

  /**
   * Verifica se a API do IBGE está disponível
   */
  private checkIbgeApiAvailability(): void {
    this.service
      .getStates()
      .pipe(take(1))
      .subscribe({
        next: () => {
          // API disponível, manter modo automático
          console.log('API do IBGE está disponível');
          // Carregar estados normalmente
          this.loadStates();
        },
        error: (error) => {
          console.error('API do IBGE indisponível:', error);
          // API indisponível, ativar modo manual automaticamente sem mostrar toast
          this.activateManualModeSilently();
        },
      });
  }

  /**
   * Salva o estado do modo manual no localStorage
   */
  private saveManualModeState(): void {
    localStorage.setItem(
      'customerManualAddressMode',
      this.isManualAddressMode.toString()
    );
  }

  /**
   * Recupera o estado do modo manual do localStorage
   */
  private loadManualModeState(): boolean {
    const saved = localStorage.getItem('customerManualAddressMode');
    return saved === 'true';
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.isDev = this.authService.getCurrentUser()?.firstName === 'dev';
    const currentPartner = this.authService.getPartner();
    // Obter IDs do token JWT e do partner
    const partner = this.authService.getPartner();
    const partnerId = this.authService.getPartnerId();
    const consultantId = this.authService.getPartnerConsultantId();
    const representativeId = this.authService.getPartnerRepresentativeId();

    console.log('[CUSTOMER CES] Partner data:', {
      partner,
      partnerId,
      consultantId,
      representativeId,
      counterId: partner?.counterId,
      unitId: partner?.unitId,
    });
    this.setLoggedPartnerAsDefault(currentPartner);

    import('../../partners/crud-index/crud-index-select.component').then(
      ({ PartnerIndexSelectComponent }) => {
        this.getFieldConfig('consultant').component =
          PartnerIndexSelectComponent;
        this.getFieldConfig('partner').component = PartnerIndexSelectComponent;
        this.getFieldConfig('representative').component =
          PartnerIndexSelectComponent;
        this.getFieldConfig('counter').component = PartnerIndexSelectComponent;
        this.getFieldConfig('unit').component = PartnerIndexSelectComponent;
      }
    );

    // Carregar estado salvo do modo manual
    const savedManualMode = this.loadManualModeState();
    if (savedManualMode) {
      this.isManualAddressMode = true;
      this.getFieldConfig('state').type = TypeComponentFieldEnum.INPUT;
      this.getFieldConfig('city').type = TypeComponentFieldEnum.INPUT;
    }

    // Verificar disponibilidade da API do IBGE antes de carregar estados
    this.checkIbgeApiAvailability();

    this.legalPersonalitySub = this.form
      .get('legalPersonality')
      ?.valueChanges.subscribe((data) => {
        if (data === LegalPersonalityEnum.PESSOA_FISICA)
          this.setToFisicPerson();
        else this.setToLegalPerson();
      });

    this.setToFisicPerson();
    this.form.get('isActive')?.setValue(true);
    this.form.get('signedAt')?.disable();
    this.branches = normalizeBranches([], this.mainAddressFromForm());
    this.watchMainAddress();
  }

  ngAfterViewInit(): void {
    this.partnerSelected = this.authService.getPartner();
    this.partnerSelectedFire.emit(this.partnerSelected);

    if (this.authService.isPartner()) {
      this.form.get('partner')?.disable();
      this.form.get('partnerCommission')?.disable();
      this.form.get('consultant')?.disable();
      this.form.get('consultantCommission')?.disable();
      this.form.get('representative')?.disable();
      this.form.get('representativeCommission')?.disable();
      this.form.get('counter')?.disable();
      this.form.get('counterCommission')?.disable();
      this.form.get('unit')?.disable();
      this.form.get('unitCommission')?.disable();

      // Desabilitar a edição dos campos no componente
      const fields = [
        'partner',
        'consultant',
        'representative',
        'counter',
        'unit',
      ];
      fields.forEach((field) => {
        const config = this.getFieldConfig(field);
        if (config) {
          config.disabled = true;
          config.componentData = {
            ...config.componentData,
            disabled: true,
          };
        }
      });
    }

    if (
      this.typeCes === TypeCesEnum.EDIT ||
      this.typeCes === TypeCesEnum.SHOW
    ) {
      this.route.params?.subscribe((params) => {
        if (params['id'])
          this.service.get(params['id']).subscribe((data: any) => {
            this.data = data;
            this.setFields();
          });
      });
    } else {
      this.setLoggedPartnerAsDefault(this.partnerSelected);
    }
  }

  private setLoggedPartnerAsDefault(partner: any): void {
    if (!partner) return;

    if (partner.isConsultant) {
      const consultantId = this.authService.getPartnerConsultantId();

      if (consultantId && consultantId !== partner.id) {
        this.partnerService
          .get(consultantId)
          .pipe(take(1))
          .subscribe((consultant: any) => {
            this.consultantSelected = consultant;
            this.form
              .get('consultant')
              ?.setValue(consultant.companyName || consultant.name);
            this.form
              .get('consultantCommission')
              ?.setValue(+consultant.consultantCommission || 0);
          });
      } else {
        this.consultantSelected = partner;
        this.form
          .get('consultant')
          ?.setValue(partner.companyName || partner.name);
        this.form
          .get('consultantCommission')
          ?.setValue(+partner.consultantCommission || 0);
      }
    }

    if (partner.isPartner) {
      const partnerId = this.authService.getPartnerId();

      if (partnerId && partnerId !== partner.id) {
        this.partnerService
          .get(partnerId)
          .pipe(take(1))
          .subscribe((realPartner: any) => {
            this.partnerSelected = realPartner;
            this.form
              .get('partner')
              ?.setValue(realPartner.companyName || realPartner.name);
            this.form
              .get('partnerCommission')
              ?.setValue(+realPartner.partnerCommission || 0);
          });
      } else {
        this.partnerSelected = partner;
        this.form.get('partner')?.setValue(partner.companyName || partner.name);
        this.form
          .get('partnerCommission')
          ?.setValue(+partner.partnerCommission || 0);
      }
    }

    if (partner.isRepresentative) {
      this.representativeSelected = partner;
      this.form
        .get('representative')
        ?.setValue(partner.companyName || partner.name);
      this.form
        .get('representativeCommission')
        ?.setValue(+partner.representativeCommission || 0);

      // Se for representante, obter o parceiro e consultor associados
      const representativeId = this.authService.getPartnerRepresentativeId();
      if (representativeId && representativeId !== partner.id) {
        // Se o ID do representante do token for diferente do parceiro atual, buscar o representante correto
        this.partnerService
          .get(representativeId)
          .pipe(take(1))
          .subscribe((representative: any) => {
            // Obter o parceiro associado ao representante
            if (representative.partner) {
              this.partnerService
                .get(representative.partner.id)
                .pipe(take(1))
                .subscribe((realPartner: any) => {
                  this.partnerSelected = realPartner;
                  this.form
                    .get('partner')
                    ?.setValue(realPartner.companyName || realPartner.name);
                  this.form
                    .get('partnerCommission')
                    ?.setValue(+realPartner.partnerCommission || 0);
                });
            }

            // Obter o consultor associado ao representante
            if (representative.consultant) {
              this.partnerService
                .get(representative.consultant.id)
                .pipe(take(1))
                .subscribe((consultant: any) => {
                  this.consultantSelected = consultant;
                  this.form
                    .get('consultant')
                    ?.setValue(consultant.companyName || consultant.name);
                  this.form
                    .get('consultantCommission')
                    ?.setValue(+consultant.consultantCommission || 0);
                });
            }
          });
      } else {
        // Obter o parceiro associado ao representante atual
        this.partnerService
          .get(partner.id)
          .pipe(take(1))
          .subscribe((fullRepresentative: any) => {
            // Obter o parceiro associado ao representante
            if (fullRepresentative.partner) {
              this.partnerSelected = fullRepresentative.partner;
              this.form
                .get('partner')
                ?.setValue(
                  this.partnerSelected.companyName || this.partnerSelected.name
                );
              this.form
                .get('partnerCommission')
                ?.setValue(+this.partnerSelected.partnerCommission || 0);
            }

            // Obter o consultor associado ao representante
            if (fullRepresentative.consultant) {
              this.consultantSelected = fullRepresentative.consultant;
              this.form
                .get('consultant')
                ?.setValue(
                  this.consultantSelected.companyName ||
                    this.consultantSelected.name
                );
              this.form
                .get('consultantCommission')
                ?.setValue(+this.consultantSelected.consultantCommission || 0);
            }
          });
      }
    }

    if (partner.isCounter) {
      this.counterSelected = partner;
      this.form.get('counter')?.setValue(partner.companyName || partner.name);
      this.form
        .get('counterCommission')
        ?.setValue(+partner.counterCommission || 0);
    }

    if (partner.isUnit) {
      this.unitSelected = partner;
      this.form.get('unit')?.setValue(partner.companyName || partner.name);
      this.form.get('unitCommission')?.setValue(+partner.unitCommission || 0);
    }
  }

  private setToFisicPerson() {
    this.removeValidators('companyName', [
      Validators.required,
      Validators.min(4),
    ]);
    this.removeValidators('cnpj', [
      Validators.required,
      Validators.minLength(14),
    ]);

    this.addValidators('name', [Validators.required, Validators.min(4)]);
    this.addValidators('cpf', [Validators.required, Validators.minLength(11)]);
    this.addValidators('generalRegistration', [
      Validators.required,
      Validators.pattern(/\d+/),
    ]);
  }

  private setToLegalPerson() {
    this.addValidators('companyName', [Validators.required, Validators.min(4)]);
    this.addValidators('cnpj', [Validators.required, Validators.minLength(14)]);

    this.removeValidators('name', [
      Validators.required,
      Validators.minLength(4),
    ]);
    this.removeValidators('cpf', [
      Validators.required,
      Validators.minLength(11),
    ]);
    this.removeValidators('generalRegistration', [
      Validators.required,
      Validators.pattern(/\d+/),
    ]);
  }

  private addValidators(name: any, fn: any) {
    const control = this.form.get(name);
    control?.addValidators(fn);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  private removeValidators(name: any, fn: any) {
    const control = this.form.get(name);
    control?.removeValidators(fn);
    control?.updateValueAndValidity({ emitEvent: false });
  }

  ngOnDestroy(): void {
    this.legalPersonalitySub?.unsubscribe();
    this.addressSyncSub.unsubscribe();
  }

  public getCitiesByUf(uf?: any): Observable<any> {
    const state = uf || this.form.get('state')?.value;
    this.form.get('city')?.reset();

    return this.service.getCitiesByUf(state);
  }

  /**
   * Ativa o modo manual silenciosamente (sem interface visual)
   */
  private activateManualModeSilently(): void {
    this.isManualAddressMode = true;

    // Modo manual: permite digitação livre nos campos
    this.getFieldConfig('state').type = TypeComponentFieldEnum.INPUT;
    this.getFieldConfig('city').type = TypeComponentFieldEnum.INPUT;

    // Limpar opções pré-definidas
    this.getFieldConfig('state').options = [];
    this.getFieldConfig('city').options = [];

    // Salvar estado no localStorage
    this.saveManualModeState();

    this.cdr.detectChanges();
  }

  /**
   * Alterna entre modo manual e automático de endereço
   * @param isManual true para modo manual, false para modo automático
   */
  public toggleAddressMode(isManual: boolean): void {
    this.isManualAddressMode = isManual;

    if (isManual) {
      // Modo manual: permite digitação livre nos campos
      this.getFieldConfig('state').type = TypeComponentFieldEnum.INPUT;
      this.getFieldConfig('city').type = TypeComponentFieldEnum.INPUT;

      // Limpar opções pré-definidas
      this.getFieldConfig('state').options = [];
      this.getFieldConfig('city').options = [];

      // Limpar valores atuais
      this.form.get('state')?.setValue('');
      this.form.get('city')?.setValue('');

      this.toastService.showInfo(
        'Modo manual ativado. Você pode digitar o estado e cidade livremente.'
      );
    } else {
      // Modo automático: volta para selects com opções da API
      this.getFieldConfig('state').type = TypeComponentFieldEnum.SELECT;
      this.getFieldConfig('city').type = TypeComponentFieldEnum.SELECT;

      // Recarregar estados
      this.loadStates();

      // Limpar cidade
      this.form.get('city')?.setValue('');
      this.getFieldConfig('city').options = [];

      this.toastService.showInfo(
        'Modo automático ativado. Carregando opções da API do IBGE.'
      );
    }

    // Salvar estado no localStorage
    this.saveManualModeState();

    this.cdr.detectChanges();
  }

  /**
   * Carrega estados com fallback para modo manual se a API falhar
   */
  public loadStates() {
    this.service
      .getStates()
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          this.getFieldConfig('state').options = [
            '',
            ...resp.map((r: any) => r.sigla).sort(),
          ];
          this.form.get('state')?.setValue('');
        },
        error: (error) => {
          console.error('Erro ao carregar estados da API:', error);
          // Ativar modo manual silenciosamente
          this.activateManualModeSilently();
        },
      });
  }

  /**
   * Modificado para suportar modo manual e automático
   */
  public onChangeState(data: string) {
    if (this.isManualAddressMode) {
      // Modo manual: não carrega cidades automaticamente
      return;
    }

    this.getCitiesByUf(data).subscribe({
      next: (resp) => {
        this.getFieldConfig('city').options = resp.map((v: any) => v.nome);
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('Erro ao carregar cidades:', error);
        // Ativar modo manual silenciosamente para cidade
        this.getFieldConfig('city').type = TypeComponentFieldEnum.INPUT;
        this.getFieldConfig('city').options = [];
        this.cdr.detectChanges();
      },
    });
  }

  public filterCities(query: string) {
    const state = this.form.get('state')?.value;
    if (!state || state == 'Todos') {
      this.form.get('city')?.reset();
      this.toastService.showWarn('Selecione o estado para filtrar cidades!');
      return;
    }
    of(query.toLowerCase())
      .pipe(
        filter((text) => text.length > 2),
        debounceTime(400),
        distinctUntilChanged(),
        map((text) =>
          this.getFieldConfig('city').options.filter((option: any) =>
            typeof option === 'object'
              ? option?.name.toLowerCase().includes(text)
              : option.toLowerCase().includes(text)
          )
        )
      )
      .subscribe((filterResult) => {
        this.filteredCities = filterResult as any;
        this.cdr.markForCheck();
      });
  }

  public onChangeCep() {
    const value = this.form.get('cep')?.value;
    if (value && value.length === 8) {
      this.service
        .getCepInfo(value)
        .pipe(take(1))
        .subscribe({
          next: (result: ICustomer) => {
            if (!this.isManualAddressMode) {
              // Modo automático: carrega cidades da API
              this.getCitiesByUf(result.state)
                .pipe(take(1))
                .subscribe({
                  next: (resp: any) => {
                    this.form.get('city')?.reset();
                    this.getFieldConfig('city').options = resp.map(
                      (v: any) => v.nome
                    );
                    this.form.patchValue({
                      ...result,
                    });
                  },
                  error: (error) => {
                    console.error('Erro ao carregar cidades do CEP:', error);
                    // Ativar modo manual silenciosamente para cidade
                    this.getFieldConfig('city').type =
                      TypeComponentFieldEnum.INPUT;
                    this.getFieldConfig('city').options = [];
                    this.form.patchValue({
                      ...result,
                    });
                  },
                });
            } else {
              // Modo manual: apenas preenche os campos com os dados do CEP
              this.form.patchValue({
                ...result,
              });
            }
          },
          error: (error) => {
            console.error('Erro ao consultar CEP:', error);
            this.toastService.showError(
              'CEP não encontrado ou erro na consulta.'
            );
          },
        });
    }
  }

  private refreshPlanSelection() {
    if (this.planSelectionDialogRef) {
      const selectedPartnerIds = this.getSelectedPartnerIds();
      this.planSelectionDialogRef.componentInstance.selectedPartnerIds =
        selectedPartnerIds;
      this.planSelectionDialogRef.componentInstance.loadInitialPlans();
    }
  }

  public selectConsultant($event: any) {
    this.consultantSelected = $event;
    this.consultantSelectedFire.emit(this.consultantSelected);
    this.form
      .get('consultantCommission')
      ?.setValue(+$event?.consultantCommission);

    if (!$event) {
      this.form.get('consultantCommission')?.setValue(0);
    }
    this.refreshPlanSelection();
  }

  public selectPartner($event: any) {
    this.partnerSelected = $event;
    this.partnerSelectedFire.emit(this.partnerSelected);
    this.form.get('partnerCommission')?.setValue(+$event?.partnerCommission);

    if (!$event) {
      this.form.get('partnerCommission')?.setValue(0);
      return;
    }

    this.partnerService
      .get($event.id)
      .pipe(take(1))
      .subscribe((fullPartner: any) => {
        if (fullPartner.consultant) {
          this.consultantSelected = fullPartner.consultant;
          this.consultantSelectedFire.emit(this.consultantSelected);
          this.form
            .get('consultant')
            ?.setValue(
              this.consultantSelected.companyName ||
                this.consultantSelected.name
            );
          this.form
            .get('consultantCommission')
            ?.setValue(+this.consultantSelected.consultantCommission || 0);
        }
      });

    this.refreshPlanSelection();

    console.log('[Select Partner teste] Partner data:', {
      partner: this.partnerSelected,
      consultant: this.consultantSelected,
      representative: this.representativeSelected,
      counter: this.counterSelected,
      unit: this.unitSelected,
    });
  }

  public selectRepresentative($event: any) {
    this.representativeSelected = $event;
    this.representativeSelectedFire.emit(this.representativeSelected);
    this.form
      .get('representativeCommission')
      ?.setValue(+$event?.representativeCommission);

    if (!$event) {
      this.form.get('representativeCommission')?.setValue(0);
      return;
    }

    // Buscar o representante com todas as relações aninhadas
    this.partnerService
      .get($event.id)
      .pipe(take(1))
      .subscribe((fullPartner: any) => {
        // Consultor (vinculado ao representante)
        if (fullPartner.consultant) {
          this.consultantSelected = fullPartner.consultant;
          this.consultantSelectedFire.emit(this.consultantSelected);
          this.form
            .get('consultant')
            ?.setValue(
              this.consultantSelected.companyName ||
                this.consultantSelected.name
            );
          this.form
            .get('consultantCommission')
            ?.setValue(+this.consultantSelected.consultantCommission || 0);
        }

        // Parceiro (vinculado ao representante)
        if (fullPartner.partner) {
          this.partnerSelected = fullPartner.partner;
          this.partnerSelectedFire.emit(this.partnerSelected);
          this.form
            .get('partner')
            ?.setValue(
              this.partnerSelected.companyName || this.partnerSelected.name
            );
          this.form
            .get('partnerCommission')
            ?.setValue(+this.partnerSelected.partnerCommission || 0);
        }
      });

    this.refreshPlanSelection();
  }

  public selectCounter($event: any) {
    this.counterSelected = $event;
    this.counterSelectedFire.emit(this.counterSelected);
    this.form.get('counterCommission')?.setValue(+$event?.counterCommission);

    if (!$event) {
      this.form.get('counterCommission')?.setValue(0);
      return;
    }

    this.partnerService
      .get($event.id)
      .pipe(take(1))
      .subscribe((fullPartner: any) => {
        if (fullPartner.consultant) {
          this.consultantSelected = fullPartner.consultant;
          this.consultantSelectedFire.emit(this.consultantSelected);
          this.form
            .get('consultant')
            ?.setValue(
              this.consultantSelected.companyName ||
                this.consultantSelected.name
            );
          this.form
            .get('consultantCommission')
            ?.setValue(+this.consultantSelected.consultantCommission || 0);
        }
      });
  }

  public selectUnit($event: any) {
    this.unitSelected = $event;
    this.unitSelectedFire.emit(this.unitSelected);
    this.form.get('unitCommission')?.setValue(+$event?.unitCommission);

    if (!$event) {
      this.form.get('unitCommission')?.setValue(0);
      return;
    }

    this.partnerService
      .get($event.id)
      .pipe(take(1))
      .subscribe((fullPartner: any) => {
        if (fullPartner.consultant) {
          this.consultantSelected = fullPartner.consultant;
          this.consultantSelectedFire.emit(this.consultantSelected);
          this.form
            .get('consultant')
            ?.setValue(
              this.consultantSelected.companyName ||
                this.consultantSelected.name
            );
          this.form
            .get('consultantCommission')
            ?.setValue(+this.consultantSelected.consultantCommission || 0);
        }
      });

    this.refreshPlanSelection();
  }

  public async save() {
    if (this.hasPendingPlan) {
      this.toastService.showWarn(
        'Você tem um plano selecionado que ainda não foi aplicado. Por favor, clique em APLICAR antes de SALVAR.'
      );
      return;
    }

    if (
      !this.consultantSelected &&
      !this.partnerSelected &&
      !this.representativeSelected
    ) {
      this.toastService.showWarn(
        'Selecione um consultor, parceiro ou representante!'
      );
      return;
    }

    if (this.form.valid) {
      let legalPersonality =
        LegalPersonalityIdEnum[
          this.form
            .get('legalPersonality')
            ?.getRawValue() as LegalPersonalityEnum
        ];

      if (this.form.get('id')?.value) {
        this.service
          .updateWithFormData(
            this.form.get('id')?.getRawValue(),
            await new FormDataUpdate().build({
              ...this.form.getRawValue(),
              consultant: this.consultantSelected?.id,
              partner: this.partnerSelected?.id,
              representative: this.representativeSelected?.id,
              counter: this.counterSelected?.id,
              unit: this.unitSelected?.id,
              legalPersonality,
              customerPlans: this.customerPlansOut?.data ?? [],
              additionalBranches: this.branches,
            })
          )
          .subscribe((data: any) => {
            CustomerService.isNavigatingToNew = false;
            this.router.navigate(['customers/show', this.data.id]);
            this.toastService.showSuccess(MessagesEnum.SUCCESS_UPDATED);
          });
      } else {
        this.service
          .createWithFormData(
            await new FormDataCreate().build({
              ...this.form.getRawValue(),
              consultant: this.consultantSelected?.id,
              partner: this.partnerSelected?.id,
              representative: this.representativeSelected?.id,
              counter: this.counterSelected?.id,
              unit: this.unitSelected?.id,
              legalPersonality,
              customerPlans: this.customerPlansOut?.data ?? [],
              additionalBranches: this.branches,
              responsibles: this.responsibleDrafts,
            })
          )
          .subscribe((data: any) => {
            CustomerService.isNavigatingToNew = false;
            this.router.navigate(['customers/show', data.id]);
            this.toastService.showSuccess(MessagesEnum.SUCCESS_CREATED);
          });
      }
    }
  }

  public edit() {
    this.router.navigate(['customers/edit', this.data.id]);
  }

  public findInvalidControls() {
    const invalid = [];
    const controls = this.form.controls;
    for (const name in controls) {
      if (controls[name].invalid) {
        invalid.push(name);
      }
    }
    return invalid;
  }

  onChangeCnpj() {
    const value = this.form.get('cnpj')?.value;

    if (value.length < 14) return;

    this.service
      .getCnpjInfo(value)
      .pipe(take(1))
      .subscribe({
        next: (result: ICustomer) => {
          this.form.patchValue({ ...result });
        },
      });
  }

  get legalPersonalityEnum() {
    return LegalPersonalityEnum;
  }

  public getLegalPersonality(type: LegalPersonalityEnum) {
    return type === this.form.get('legalPersonality')?.value;
  }

  protected getSelectedPartnerIds(): string[] {
    const partnerIds: string[] = [];

    if (this.partnerSelected?.id) {
      partnerIds.push(this.partnerSelected.id);
    }

    if (
      this.consultantSelected?.id &&
      !partnerIds.includes(this.consultantSelected.id)
    ) {
      partnerIds.push(this.consultantSelected.id);
    }

    if (
      this.representativeSelected?.id &&
      !partnerIds.includes(this.representativeSelected.id)
    ) {
      partnerIds.push(this.representativeSelected.id);
    }

    return partnerIds;
  }

  public openPlanSelectionDialog(): void {
    const selectedPartnerIds = this.getSelectedPartnerIds();

    this.planSelectionDialogRef = this.dialog.open(
      PlanSelectionWizardComponent,
      {
        width: '80%',
        height: '90%',
        data: {
          preSelectedPlan: null,
          preSelectedSystem: null,
          preSelectedModules: [],
          selectedPartnerIds: selectedPartnerIds,
        },
      }
    );

    this.planSelectionDialogRef.afterClosed().subscribe((result) => {
      if (result) {
      }
      this.planSelectionDialogRef = null;
    });
  }

  /**
   * Abre o link do arquivo quando o usuário clica no nome do arquivo
   * @param fileUrl URL do arquivo para abrir
   */
  public openFileLink(fileUrl: string): void {
    if (fileUrl && typeof fileUrl === 'string') {
      // Verificar se já contém o prefixo do S3
      if (fileUrl.startsWith('http')) {
        window.open(fileUrl, '_blank');
      } else {
        // Adicionar prefixo do S3 se necessário
        window.open(S3_URL_BASE + fileUrl, '_blank');
      }
    }
  }

  /**
   * Exibe o diálogo de confirmação para excluir um arquivo
   * @param fileUrl URL do arquivo a ser excluído
   * @param fieldName Nome do campo no formulário
   */
  public confirmDeleteFile(fileUrl: string, fieldName: string): void {
    if (!fileUrl || !fieldName) return;

    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      width: '400px',
      data: {
        title: 'Confirmação',
        content: `Tem certeza que deseja remover este arquivo?`,
        yesText: 'Sim, remover',
        noText: 'Cancelar',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.result === true) {
        // Remove o arquivo do campo específico
        this.form.get(fieldName)?.setValue(null);

        // Limpar também o comentário e a data
        this.form.get(`${fieldName}Comment`)?.setValue(null);
        this.form.get(`${fieldName}Date`)?.setValue(null);

        this.toastService.showSuccess('Arquivo removido com sucesso');
      }
    });
  }
}
